import { useAuth } from '@teko/auth-react';
import { DiscoveryProduct, SearchProductPagination } from '@teko/discovery';
import { SearchV2Params, useDiscoverySearchV2 } from '@teko/discovery/v2';
import { ProductCardProps } from '@teko/types/ecommerce';
import {
  useBuildPCV2,
  useCartV2,
  useFrame,
  useRelatedProduct,
  useServices,
  useTenantConfig,
} from 'contexts';
import { createHandleEventAndCtaButton } from 'helpers';
import { useHandlerUnderEighteenYearOld } from 'hooks/useHandlerUnderEighteenYearOld';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

export type UsePbProductReturns<T = DiscoveryProduct> = {
  products: T[];
  pagination?: SearchProductPagination;
};

export const useSearchProductDiscovery = (
  searchPayload: SearchV2Params,
  fallbackData?: {
    products: ProductCardProps[];
  },
) => {
  const {
    isDisplayUom,
    contactLink,
    isAllowATCFromListing,
    showNewProductGift,
    epicOmni1524,
  } = useTenantConfig();
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { frames } = useFrame();
  const cartV2 = useCartV2();
  const buildPCV2 = useBuildPCV2();
  const { isMobile } = useTenantConfig();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const { loading: authLoading } = useAuth();
  const router = useRouter();
  const relatedProductHook = useRelatedProduct();

  const isEnableNewBuildPCPage = useMemo(() => {
    return router.pathname.includes('/buildpc') && !!epicOmni1524;
  }, [router.pathname, epicOmni1524]);

  const discoveryCleanUpConfig = {
    showUom: isDisplayUom,
    contactLink,
    showCtaButton: isAllowATCFromListing,
    cartV2,
    buildPCV2,
    frames,
    showNewProductGift,
    showCtaButtons: isAllowATCFromListing && isEnableNewBuildPCPage,
    showCtaButtonsWithQuantity: isEnableNewBuildPCPage,
    isMobile,
    relatedProductHook,
  };

  const { result, ...rest } = useDiscoverySearchV2(
    discoveryServiceV2,
    searchPayload && Object.keys(searchPayload).length
      ? {
          ...searchPayload,
          pageSize: 20,
          isReturnProductsBOMSkus: isEnableNewBuildPCPage,
        }
      : {},
    {
      fallbackData: fallbackData,
      transform: (data) => ({
        products: data.products?.map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
            ...discoveryCleanUpConfig,
          }),
        ),
      }),
    },
  );

  const productList = useMemo(() => {
    return (result?.products || [])
      .map((p) =>
        createHandleEventAndCtaButton(p, {
          ...discoveryCleanUpConfig,
        }),
      )
      .map(transformProductWhenUserUnder18);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    JSON.stringify(result),
    JSON.stringify(cartV2.flattenItems),
    authLoading,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return {
    products: productList,
    ...rest,
  };
};
